import { DateTime } from 'luxon';
import React, {
  useCallback,
  useEffect,
  useReducer,
  useState
} from 'react';
import { useIntl } from 'react-intl';
import { connect } from 'react-redux';

import {
  CircularProgress,
  IconButton,
  Link,
  makeStyles,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip
} from '@material-ui/core';
import {
  green,
  grey,
  yellow
} from '@material-ui/core/colors';

import {
  Check as IconSaved,
  Clear as IconClear,
  Forward as IconUpdate,
  PriorityHigh as IconHasUnsavedChanges,
  Undo as IconRevert
} from '@material-ui/icons';

import {
  ChecklistAutocomplete,
  ChecklistAutocompleteOption
} from '@checkit/react-components';

import {
  getCheckForSnapshot,
  getCheckVersion
} from '../../checks/actions';
import {
  Check,
  EntitySnapshotForCheck
} from '../../checks/state';
import { loaderReducer } from '../../common/delayedLoader';
import config from '../../config';
import { useAxiosDispatch } from '../../middleware/axios';
import { State } from '../../state';

import {
  RenderEntityAutocompleteContext,
  RenderEntityTableContext,
  SelectedEntityPanel
} from './SelectedEntityPanel';
import { messages } from './translations';

const useStyles = makeStyles(( ) => ({
  cellIcon: { width: '46px' },
  cellName: {
    // used to give table rows some vertical padding, while using mui's paddingNone to avoid the horizontal padding
    padding: '0.65em 0',
    width: '33%'
  },
  // used to vertically center the inline icons inside table cells
  icon: { marginTop: '0.4em' },
  // fix the table column widths manually to avoid jumping around as contents change
  table: { tableLayout: 'fixed' },
  // background colour applies to check rows with unsaved changes, to warn the user
  unsaved: { background: yellow['50'] }
}));

const formatDate = (date: DateTime): string => date.toLocaleString(DateTime.DATETIME_MED);
const getCheckUrl = (check: Check): string => `${config.controlCentre.url}/workmanagement/checklists/owned/${check.id.entityId}`;

/** A component representing a single check shown in the checks list. */
const SelectedChecksTableItem = connect(({ checks }: State) => ({ checks }))(function SelectedChecksTableItemImpl({
  snapshot,
  checks,
  isUnsaved,
  isDeletable,
  previousVersionCheck,
  handleDelete,
  handleUpdate,
  handleUndo
}: {
  /** The check entity snapshot represented by this table item. */
  snapshot: EntitySnapshotForCheck;
  /** All fetched checks, from global redux state. */
  checks: State['checks'];
  /** Whether the check is unsaved. */
  isUnsaved: boolean;
  /** Whether the check is deletable. */
  isDeletable: boolean;
  /** Previous version check. */
  previousVersionCheck: EntitySnapshotForCheck | null;
  /** Removes the check corresponding to the given snapshot from the library's selected checks. */
  handleDelete: (snapshot: EntitySnapshotForCheck) => void;
  /** updates the check to the previous version. */
  handleUndo: (snapshot: EntitySnapshotForCheck, replacement: EntitySnapshotForCheck) => void;
  /** Updates the given snapshot to the given replacement snapshot in the library's selected checks. */
  handleUpdate: (snapshot: EntitySnapshotForCheck, replacement: EntitySnapshotForCheck) => void;
}) {
  const { formatMessage } = useIntl( );
  const dispatch = useAxiosDispatch( );
  const classes = useStyles();

  const [ checkSnapped, setCheckSnapped ] = useState<Check | null>(null);
  const [ checkLatest, setCheckLatest ] = useState<Check | null>(null);
  const [ loadingState, loadingDispatch ] = useReducer(loaderReducer, {
    displayLoader: true,
    loading: false
  });

  /** Populates the check's data once it has been fetched. */
  const handleLoaded = useCallback(function populate( ) {
    const latestCode = checks.latestCodeById[snapshot.id];
    if (latestCode == null) {
      throw new Error('Latest check version not loaded, but handleLoaded() called');
    }

    const snapped = checks.byCode[snapshot.code];
    const latest = checks.byCode[latestCode];
    if (snapped == null) {
      throw new Error('Snapshotted check not loaded, but handleLoaded() called');
    }
    if (latest == null) {
      throw new Error('Latest check not loaded, but handleLoaded() called');
    }

    // populate check data
    setCheckSnapped(snapped);
    setCheckLatest(latest);

    // display data
    loadingDispatch('loaded');
  }, [checks, snapshot]);

  useEffect(function load( ) {
    // load both the check version which has been snapshotted, as well as the
    // check's latest version

    const isLoadedSnapped = snapshot.code in checks.byCode;
    const isLoadedLatest = snapshot.id in checks.latestCodeById;
    const isLoaded = isLoadedSnapped && isLoadedLatest;

    const isLoading = loadingState.loading;

    if (isLoaded) {
      handleLoaded( );
    } else if (!isLoading) {
      loadingDispatch('loading');

      if (!isLoadedSnapped) {
        dispatch(getCheckForSnapshot(snapshot));
      }
      if (!isLoadedLatest) {
        dispatch(getCheckVersion(snapshot.id, 'latest'));
      }
    }
  }, [checks.byCode, checks.latestCodeById, dispatch, handleLoaded, loadingState.loading, snapshot]);

  /** Updates this check's snapshot to use the latest existing version. */
  const onClickUpdate = useCallback(function onClickUpdateHandler( ) {
    if (checkLatest == null) {
      throw new Error(`Check ${snapshot.code} updated before latest check was loaded`);
    }
    handleUpdate(snapshot, {
      code: checkLatest.code,
      id: checkLatest.id.entityId,
      type: 'check',
      version: checkLatest.id.version
    });
  }, [handleUpdate, snapshot, checkLatest]);
  /** Undo this check's snapshot to use the previous version. */
  const onClickUndo = useCallback(function onClickUndoHandler( ) {
    if (!previousVersionCheck ) {
      throw new Error(`Check ${snapshot.code} updated before previous version was loaded`);
    }
    handleUndo(snapshot, previousVersionCheck);
  }, [handleUndo, snapshot, previousVersionCheck]);
  /** Removes this check from the library. */
  const onClickDelete = useCallback(function onClickDeleteHandler( ) {
    handleDelete(snapshot);
  }, [handleDelete, snapshot]);

  if (loadingState.displayLoader) {
    return (
      <TableRow>
        <TableCell align="center" colSpan={4}>
          <CircularProgress color="inherit" size={12} />
        </TableCell>
      </TableRow>
    );
  }

  if (checkSnapped == null) {
    throw new Error(`SelectedChecksTableItem(${snapshot.code}) loaded before snapshotted check was loaded`);
  }
  if (checkLatest == null) {
    throw new Error(`SelectedChecksTableItem(${snapshot.code}) loaded before latest check was loaded`);
  }

  const statusIcon = isUnsaved ? (
    <Tooltip title={formatMessage(messages['check.table.item.status.unsaved'])}>
      <IconHasUnsavedChanges className={classes.icon} htmlColor={grey['600']} fontSize="inherit" />
    </Tooltip>
  ) : (
    <Tooltip title={formatMessage(messages['check.table.item.status.saved'])}>
      <IconSaved className={classes.icon} htmlColor={green['500']} fontSize="inherit" />
    </Tooltip>
  );

  const versionSnapped = DateTime.fromMillis(checkSnapped.id.version);
  const versionLatest = DateTime.fromMillis(checkLatest.id.version);

  const hasOlderVersion = previousVersionCheck !== null;
  const hasNewerVersion = checkSnapped.id.version !== checkLatest.id.version;

  const versionCell = hasNewerVersion ? (
    <Tooltip title={formatMessage(messages['check.table.item.hint.hasupdate'])}>
      <span>{formatDate(versionSnapped)} <strong>*</strong></span>
    </Tooltip>
  ) : (<span>{formatDate(versionSnapped)}</span>);

  const updateCell = hasNewerVersion ? (
    <Tooltip title={formatMessage(messages['check.table.item.version.update'])}>
      <IconButton
        aria-label="update"
        size="small"
        onClick={onClickUpdate}
      >
        <IconUpdate fontSize="inherit" />
      </IconButton>
    </Tooltip>
  ) : hasOlderVersion ? (
    <Tooltip title={formatMessage(messages['check.table.item.version.revert'])}>
      <IconButton
        aria-label="undo"
        size="small"
        onClick={onClickUndo}
      >
        <IconRevert fontSize="inherit" />
      </IconButton>
    </Tooltip>
  ) : null;

  const deleteCell = isDeletable ? (
    <Tooltip title={formatMessage(messages['check.table.item.remove'])}>
      <IconButton
        aria-label="delete"
        size="small"
        onClick={onClickDelete}
      >
        <IconClear fontSize="inherit" />
      </IconButton>
    </Tooltip>
  ) : (
    <IconButton
      aria-label="delete"
      size="small"
      onClick={onClickDelete}
      disabled={true}
    >
      <IconClear fontSize="inherit" />
    </IconButton>
  );

  return (
    <TableRow className={isUnsaved ? classes.unsaved : ''}>
      <TableCell padding="checkbox">{statusIcon}</TableCell>
      <TableCell className={classes.cellName} component="th" scope="row">{checkSnapped.name}</TableCell>
      <TableCell align="right">
        <Link href={getCheckUrl(checkLatest)} target="_blank">{formatDate(versionLatest)}</Link>
      </TableCell>
      <TableCell padding="checkbox">{updateCell}</TableCell>
      <TableCell>{versionCell}</TableCell>
      <TableCell padding="checkbox">{deleteCell}</TableCell>
    </TableRow>
  );
});

/** A table of checks selected by the user for inclusion in a library. */
function SelectedChecksTable({
  checks,
  isUnsaved,
  isDeletable,
  getPreviousVersionCheck,
  handleDelete,
  handleUpdate,
  handleUndo
}: {
  /** An array of check codes corresponding to the checks to display in the table. */
  checks: EntitySnapshotForCheck[],
  /** A callback which handles the logic to decide whether a given check is deletable. */
  isDeletable: (check: EntitySnapshotForCheck) => boolean;
  /** A callback which handles the logic to decide whether a given check is unsaved. */
  isUnsaved: (check: EntitySnapshotForCheck) => boolean;
  /** Removes the check corresponding to the given snapshot from the library's selected checks. */
  handleDelete: (snapshot: EntitySnapshotForCheck) => void;
  /** updates the check to the previous version. */
  handleUndo: (snapshot: EntitySnapshotForCheck, replacement: EntitySnapshotForCheck) => void;
  /** A callback to get the previous version check. */
  getPreviousVersionCheck: (snapshot: EntitySnapshotForCheck) => EntitySnapshotForCheck | null;
  /** Updates the given snapshot to the given replacement snapshot in the library's selected checks. */
  handleUpdate: (snapshot: EntitySnapshotForCheck, replacement: EntitySnapshotForCheck) => void;
}) {
  const { formatMessage } = useIntl( );
  const classes = useStyles( );

  const items = checks.map((snapshot) => (
    <SelectedChecksTableItem
      key={snapshot.id}
      snapshot={snapshot}
      isUnsaved={isUnsaved(snapshot)}
      handleDelete={handleDelete}
      handleUndo={handleUndo}
      handleUpdate={handleUpdate}
      isDeletable={isDeletable(snapshot)}
      previousVersionCheck={getPreviousVersionCheck(snapshot)}
    />
  ));

  return (
    <TableContainer component={Paper}>
      <Table
        className={classes.table}
        size="small"
        padding="none"
        aria-label={formatMessage(messages['check.table.label'])}
      >
        <TableHead>
          <TableRow>
            <TableCell className={classes.cellIcon} />
            <TableCell className={classes.cellName}>{formatMessage(messages['check.table.header.name'])}</TableCell>
            <TableCell align="right">{formatMessage(messages['check.table.header.version'])}</TableCell>
            <TableCell className={classes.cellIcon} />
            <TableCell>{formatMessage(messages['check.table.header.snapshot'])}</TableCell>
            <TableCell className={classes.cellIcon} />
          </TableRow>
        </TableHead>
        <TableBody>{items}</TableBody>
      </Table>
    </TableContainer>
  );
}

/** An expandable form panel for selecting checks for inclusion in a library. */
export function SelectedChecksPanel({ selected, setSelected, existing, isLibrarySaved, isLibraryPublished }: {
  /** The checks which have been selected. */
  selected: EntitySnapshotForCheck[];
  /** A callback used to signal the fact that the selected checks have changed. */
  setSelected: (selection: EntitySnapshotForCheck[]) => void;
  /** The checks which are already present in the server-persisted version of the library. */
  existing: EntitySnapshotForCheck[];
  /** Whether the library these checks are in has actually been saved to the server. */
  isLibrarySaved: boolean;
  /** Whether the library these checks are in has already been published to customers. */
  isLibraryPublished: boolean;
}) {
  const { formatMessage } = useIntl( );
  const heading = formatMessage(messages['check.form.heading']);

  const isCheckUnsaved = useCallback(function isUnsaved(check) {
    if (!isLibrarySaved) {
      return true;
    }

    // a check is unsaved if it isn't in the list of pre-existing checks
    return !existing.some((e) => e.code === check.code);
  }, [ isLibrarySaved, existing ]);

  const isCheckDeletable = useCallback(function isDeletable(check) {
    if (!isLibraryPublished) {
      // if the library isn't published, no reason to not allow deleting checks
      return true;
    }

    // otherwise, only allow deleting a check if it is newly added to the library
    // regardless of version, therefore comparing entity id rather than code
    return !existing.some((e) => e.id === check.id);
  }, [ isLibraryPublished, existing ]);

  const getPreviousVersionCheck = useCallback(function previousVersionCheck(check) {
    const checkFound = existing.find((e) => ((e.id === check.id) && (e.code !== check.code)));
    return checkFound || null;
  }, [ existing ]);

  const renderAutocomplete = useCallback(({ handleAdd }: RenderEntityAutocompleteContext<'check'>) => {
    const onAutocompleteOptionSelected = (
      _: React.ChangeEvent<{ }>,
      option: ChecklistAutocompleteOption | null
    ) => option ? handleAdd({
      code: option.latest.code,
      id: option.code,
      type: 'check',
      version: option.latest.version
    }) : null;

    return (
      <ChecklistAutocomplete
        label={formatMessage(messages['check.form.autocomplete'])}

        excluded={selected.map((e) => e.id)}
        onChange={onAutocompleteOptionSelected}

        clearOnSelect={true}
        fullWidth={true}
      />
    );
  }, [selected, formatMessage]);

  const renderTable = ({ handleDelete, handleUpdate, handleUndo }: RenderEntityTableContext<'check'>) => (
    <SelectedChecksTable
      checks={selected}
      isUnsaved={isCheckUnsaved}
      isDeletable={isCheckDeletable}
      handleDelete={handleDelete}
      handleUpdate={handleUpdate}
      handleUndo={handleUndo}
      getPreviousVersionCheck={getPreviousVersionCheck}
    />
  );

  return (
    <SelectedEntityPanel
      type="check"
      heading={heading}

      selected={selected}
      setSelected={setSelected}
      isLibrarySaved={isLibrarySaved}

      Autocomplete={renderAutocomplete}
      Table={renderTable}
    />
  );
}
